import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import Router from 'next/router';
import { SearchApi as PublicationsApi } from '@api/publications/search';
import { CriteriaData } from '@models/CriteriaData';
import ISTData from '@models/ISTData';
import { VignettePublicationCard } from './VignettePublicationCard';

export interface VignetteConfig {
  /** Short label shown above the title, e.g. "TECHNOLOGY DOMAIN" */
  eyebrow: string;
  /** Display title of the vignette, e.g. "Artificial Intelligence" */
  title: string;
  /** Short description of this domain */
  description: string;
  /** Emoji or short symbol used as an icon */
  icon: string;
  /** Search criteria sent to the publications API (English + Chinese terms) */
  criteria: CriteriaData[];
  /** Accent colour for the vignette border / icon background */
  accentColor: string;
}

export interface VignetteSectionProps {
  config: VignetteConfig;
  /** Number of publications to display per page (default: 6) */
  pageSize?: number;
}

const MAX_VIGNETTE_PAGES = 20;

export const VignetteSection: FC<VignetteSectionProps> = ({
  config,
  pageSize = 6,
}) => {
  const [publications, setPublications] = useState<ISTData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    PublicationsApi.generalSearch(
      config.criteria,
      (page - 1) * pageSize,
      { publication_date: 'desc' },
      pageSize,
    )
      .then((res) => {
        setPublications(res.data?.stData ?? []);
        const total = res.data?.totalPublications ?? 0;
        const pages = Math.min(Math.ceil(total / pageSize), MAX_VIGNETTE_PAGES);
        setTotalPages(pages >= 1 ? pages : 1);
      })
      .catch(() => {
        setPublications([]);
        setTotalPages(1);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [config.criteria, pageSize, page]);

  const renderPublications = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 120,
          }}
        >
          <CircularProgress size={24} sx={{ color: '#6366f1' }} />
        </Box>
      );
    }
    if (publications.length === 0) {
      return (
        <Typography sx={{ color: '#4b5563', fontSize: '0.82rem', p: 1 }}>
          No recent publications found.
        </Typography>
      );
    }
    return (
      <>
        <Grid container spacing={1.5}>
          {publications.map((pub) => (
            <Grid item xs={12} key={pub.id}>
              <VignettePublicationCard publication={pub} />
            </Grid>
          ))}
        </Grid>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Pagination
              page={page}
              count={totalPages}
              size="small"
              onChange={(_, value) => setPage(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#94a3b8',
                  borderColor: 'rgba(148,163,184,0.2)',
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: 'rgba(99,102,241,0.25)',
                  color: '#c7d2fe',
                },
              }}
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid rgba(148, 163, 184, 0.12)`,
        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(8px)',
        p: { xs: 2.5, md: 3 },
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Vignette header */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              fontSize: '1.4rem',
              lineHeight: 1,
              mt: 0.25,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1.5,
              background: `${config.accentColor}18`,
              border: `1px solid ${config.accentColor}33`,
              flexShrink: 0,
            }}
          >
            {config.icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: config.accentColor,
                mb: 0.25,
              }}
            >
              {config.eyebrow}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#f1f5f9',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {config.title}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.76rem',
                color: '#64748b',
                mt: 0.25,
                lineHeight: 1.4,
              }}
            >
              {config.description}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          variant="text"
          onClick={() =>
            Router.push({
              pathname: '/search/publications',
              query: { criteria: JSON.stringify(config.criteria) },
            })
          }
          sx={{
            color: '#818cf8',
            textTransform: 'none',
            fontSize: '0.75rem',
            flexShrink: 0,
            ml: 1,
            '&:hover': {
              backgroundColor: 'rgba(99,102,241,0.08)',
            },
          }}
        >
          View all
        </Button>
      </Stack>

      {/* Publication list */}
      {renderPublications()}
    </Box>
  );
};
